import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import type { CoverageAddress } from 'hydra-data-types';
import { isNonEmptyString, stripUndefined } from 'ts-utils';
import { Environment } from '../util/Environment';
import { recordError } from '../util/Errors';

/**
 * Referrer-restricted browser keys for the Maps JavaScript API + Places API (New), one per Firebase
 * project (nerva-dev for local/dev; ibs-app-d4d2a serves staging and production). Like the Firebase
 * web keys they ship to the browser and are public by design; the HTTP referrer and API
 * restrictions in GCP are what protect them.
 */
const DEVELOPMENT_PLACES_API_KEY = 'AIzaSyCjx98nRjkM5CqFQz_r_aBI5yXnQM8CC6U';
const PRODUCTION_PLACES_API_KEY = 'AIzaSyDofTVGC97zMUpS61bicU-rrOa-ciS04KU';

export function getGooglePlacesApiKey(): string | undefined {
  // Cypress must never load Google: CI has no referrer allow-listed and autopilot uses the
  // not-found path anyway.
  if (Environment.E2E_TESTING) return undefined;
  return Environment.DEVELOPMENT ? DEVELOPMENT_PLACES_API_KEY : PRODUCTION_PLACES_API_KEY;
}

let placesLibrary: Promise<google.maps.PlacesLibrary | undefined> | undefined;

/**
 * Loads the Places library once per page. Resolves `undefined` when there is no key for this
 * environment or the script fails to load, so callers degrade to "no suggestions" rather than
 * blocking the flow. A failed load is not memoised, so a later screen mount can retry.
 */
export function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary | undefined> {
  if (placesLibrary === undefined) {
    placesLibrary = load();
  }
  return placesLibrary;
}

async function load(): Promise<google.maps.PlacesLibrary | undefined> {
  const key = getGooglePlacesApiKey();
  if (key === undefined) return undefined;
  try {
    setOptions({ key, v: 'weekly' });
    return await importLibrary('places');
  } catch (error) {
    recordError(error);
    placesLibrary = undefined;
    return undefined;
  }
}

/** Only street-level places: no cities, businesses or POIs in the suggestion list. */
const ADDRESS_PRIMARY_TYPES = ['street_address', 'premise', 'subpremise'];

export async function fetchAddressSuggestions(
  places: google.maps.PlacesLibrary,
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken
): Promise<google.maps.places.PlacePrediction[]> {
  const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    sessionToken,
    includedRegionCodes: ['us'],
    includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
    language: 'en-US',
    region: 'us',
  });
  return suggestions.flatMap((suggestion) =>
    suggestion.placePrediction === null ? [] : [suggestion.placePrediction]
  );
}

/** The subset of {@link google.maps.places.AddressComponent} the mapper reads (plain objects in tests). */
export type AddressComponentLike = Pick<
  google.maps.places.AddressComponent,
  'longText' | 'shortText' | 'types'
>;

/**
 * Google omits `locality` for some US addresses (e.g. New York boroughs, unincorporated areas), so
 * fall through the next-most-specific place names in order.
 */
const CITY_COMPONENT_TYPES = [
  'locality',
  'postal_town',
  'sublocality_level_1',
  'administrative_area_level_3',
  'neighborhood',
];

/**
 * Maps Google address components to the structured address we persist. Returns `undefined` when
 * any of street, city, state or ZIP is missing so a partial selection can't be stored as complete.
 */
export function toCoverageAddress(
  components: readonly AddressComponentLike[],
  formattedAddress: string,
  placeId?: string
): CoverageAddress | undefined {
  function find(type: string, field: 'longText' | 'shortText' = 'longText'): string | undefined {
    const value = components.find((component) => component.types.includes(type))?.[field];
    return isNonEmptyString(value) ? value : undefined;
  }

  const line1 = [find('street_number'), find('route')].filter(isNonEmptyString).join(' ');
  const line2 = find('subpremise');
  const city = CITY_COMPONENT_TYPES.map((type) => find(type)).find(isNonEmptyString);
  const state = find('administrative_area_level_1', 'shortText');
  // Keep the 5-digit ZIP only; a ZIP+4 suffix sometimes arrives inside `postal_code` itself.
  const postalCode = find('postal_code')?.match(/^\d{5}/)?.[0];

  if (
    !isNonEmptyString(line1) ||
    !isNonEmptyString(city) ||
    !isNonEmptyString(state) ||
    !isNonEmptyString(postalCode)
  ) {
    return undefined;
  }

  // Firestore rejects `undefined` field values, so drop the optional keys when absent.
  return stripUndefined({ line1, line2, city, state, postalCode, placeId, formattedAddress });
}

/**
 * Resolves a prediction to a structured address. The session token used for the suggestions is
 * carried into this `fetchFields` call automatically and the billing session ends here.
 */
export async function fetchCoverageAddress(
  prediction: google.maps.places.PlacePrediction
): Promise<CoverageAddress | undefined> {
  const { place } = await prediction
    .toPlace()
    .fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
  return toCoverageAddress(
    place.addressComponents ?? [],
    place.formattedAddress ?? prediction.text.text,
    place.id
  );
}
